import {
  AloftDate,
  LocationType,
  MLSListing,
  PolygonGeoSearch,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEdgeTrigger } from '../../../hooks/useEdgeTrigger.ts';
import { useZodFormContext } from '../../../hooks/useZodForm.ts';
import { countActiveFilters } from '../../FilterDrawer/countActiveFilters.ts';
import { LoadingScreenSam } from '../../LoadingScreen/LoadingScreenSam.tsx';
import { useRecommendedProperties } from '../hooks/useRecommendedProperties.ts';
import { useSubjectPropertyToCompSelectionProperty } from '../hooks/useSubjectPropertyToCompSelectionProperty.ts';
import {
  CompSearch,
  CompSelectionProperty,
  RecommendCompsFormSchema,
  RecommendedCompSelectionProps,
} from '../types.ts';
import { NewSimilarityScoreCalcOptions } from '../utils/addSimilarityScores.ts';
import {
  MAX_COMPS,
  buildRentalRecommendationLists,
} from '../utils/buildRentalRecommendationLists.ts';
import { compsToCompSelectionProps } from '../utils/compsToCompSelectionProps.ts';
import { convertRecommendedCompsToCSV } from '../utils/convertRecommendedCompsToCSVData.ts';
import { CompSelection } from './CompSelection.tsx';
import { MissingDataWarning } from './MissingDataWarning.tsx';
import { NoRecommendationsAvailable } from './NoRecommendationsAvailable.tsx';

const INITIAL_VISIBLE_COMPS = 20;

const RentalRecCompSelectionInner = forwardRef(
  (
    {
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      listings,
      subjectComp,
      comps,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      newCalcOptions,
      updateCompsLabel,
      onUpdateComps,
      onDeselectAll,
    }: Pick<
      RecommendedCompSelectionProps,
      | 'sorting'
      | 'setSorting'
      | 'rowSelection'
      | 'setRowSelection'
      | 'orderID'
      | 'updateCompsLabel'
      | 'onUpdateComps'
      | 'onDeselectAll'
    > & {
      subjectComp: CompSelectionProperty;
      comps: CompSelectionProperty[];
      listings: MLSListing[];
      isInspectifyOrder: boolean;
      hasArvOption: boolean;
      newCalcOptions?: NewSimilarityScoreCalcOptions;
    },
    ref,
  ) => {
    const selectedRecommendationsByIdRef = useRef<Record<string, CompSelectionProperty>>({});

    const { recommendations, filteredRecs } = useMemo(
      () =>
        buildRentalRecommendationLists(subjectComp, comps, listings, hasArvOption, newCalcOptions),
      [listings, subjectComp, comps, hasArvOption, newCalcOptions],
    );

    const dataWithSelectedComps = useMemo(() => {
      const makeKey = (c: CompSelectionProperty) =>
        (c.listingID ?? c.address?.AddressLineText ?? '').toLowerCase();
      const seen = new Set<string>();
      const isSelected = (id: string) => {
        const selection = rowSelection[id];
        if (!selection) return false;
        if (!isInspectifyOrder || !hasArvOption) return true;
        return !!selection.aiv || !!selection.arv;
      };

      const nextSelectedRecommendationsById = { ...selectedRecommendationsByIdRef.current };
      Object.keys(nextSelectedRecommendationsById).forEach((id) => {
        if (!isSelected(id)) {
          delete nextSelectedRecommendationsById[id];
        }
      });
      [...comps, ...recommendations].forEach((c) => {
        if (isSelected(c.id)) {
          nextSelectedRecommendationsById[c.id] = c;
        }
      });
      selectedRecommendationsByIdRef.current = nextSelectedRecommendationsById;

      const unique = (items: CompSelectionProperty[]) =>
        items.filter((c) => {
          const key = makeKey(c);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      const remainingFiltered = unique(filteredRecs);
      const selectedUnique = unique(comps);
      const selectedFromRecommendations = unique(Object.values(nextSelectedRecommendationsById));

      return [...selectedUnique, ...selectedFromRecommendations, ...remainingFiltered];
    }, [comps, filteredRecs, recommendations, rowSelection, isInspectifyOrder, hasArvOption]);

    const downloadCSV = () => {
      const csvContent = convertRecommendedCompsToCSV(dataWithSelectedComps);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recommended_rental_comps_order_${orderID}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    useImperativeHandle(ref, () => ({
      downloadCSV,
      getQualityTiersBreakdown: () => ({}),
    }));

    const [isExpanded, setIsExpanded] = useState(false);

    const totalComps = Math.min(dataWithSelectedComps.length, MAX_COMPS);
    const visibleData = useMemo(
      () => dataWithSelectedComps.slice(0, isExpanded ? MAX_COMPS : INITIAL_VISIBLE_COMPS),
      [dataWithSelectedComps, isExpanded],
    );

    const handleExpand = useCallback(() => setIsExpanded(true), []);
    const handleCollapse = useCallback(() => setIsExpanded(false), []);

    return (
      <CompSelection
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={visibleData}
        subject={subjectComp}
        comps={comps}
        source="abode"
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        compSelectionTableVariant="rental"
        isExpanded={isExpanded}
        totalComps={totalComps}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
        updateCompsLabel={updateCompsLabel}
        onUpdateComps={onUpdateComps}
        onDeselectAll={onDeselectAll}
      />
    );
  },
);

export const RentalRecommendedCompSelection = forwardRef(
  (
    {
      orderID,
      subject,
      effectiveDate,
      comps,
      compSeq,
      getDefaultFormValues,
      showNoRecommendations,
      setShowNoRecommendations,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      isInspectifyOrder,
      hasArvOption,
      currentComps: _currentComps,
      allowRecommendedCompsFiltering: _allowRecommendedCompsFiltering,
      onClose,
      allowNewSimilarityScore,
      similarityScoreWeights,
      updateCompsLabel,
      onUpdateComps,
      onDeselectAll,
    }: RecommendedCompSelectionProps,
    ref,
  ) => {
    const effectiveDateAloft = AloftDate.fromIsoString(effectiveDate.isoString);
    const { isIdle, isLoading, isError, data, getProperties } = useRecommendedProperties({
      orderID,
      compType: 'rental',
    });
    const subjectComp = useSubjectPropertyToCompSelectionProperty(subject, effectiveDateAloft);

    const subjectLocationType: LocationType =
      subject.editableData.propertyIdentification.locationType ?? 'Urban';
    const locationTypeWeights = similarityScoreWeights?.[subjectLocationType];

    const newCalcOptions: NewSimilarityScoreCalcOptions | undefined =
      allowNewSimilarityScore && locationTypeWeights
        ? { weights: locationTypeWeights, effectiveDate: effectiveDateAloft }
        : undefined;

    const isSubjectGlaDefined = isDefined(subject.editableData.improvements.gla);
    const isSubjectLotSizeDefined =
      isDefined(subject.editableData.site.area) && subject.editableData.site.area.unitAmount > 0;
    const isSubjectLocationDefined =
      isDefined(subjectComp.latitude) && isDefined(subjectComp.longitude);
    const { getValues, setValue } = useZodFormContext<RecommendCompsFormSchema>();

    const isRunnable = isSubjectGlaDefined && isSubjectLotSizeDefined && isSubjectLocationDefined;

    const getSearchCriteria = () => {
      const compSearch = getValues().search as CompSearch;
      const coordinates = (getValues().search.geoSearch as PolygonGeoSearch).coords;
      return { search: compSearch, coordinates };
    };

    useEdgeTrigger({
      condition: !isLoading && isIdle && isRunnable && isUndefined(data),
      action: () => {
        const { search } = getValues();
        if (countActiveFilters(search.filters) === 0) {
          const formSearch = getDefaultFormValues();
          setValue('search', formSearch);
        }
        setShowNoRecommendations(false);
        getProperties(getSearchCriteria());
      },
    });

    useEffect(() => {
      if (
        data?.body &&
        'status' in data.body &&
        (data.body.status === 'not_found' || data.body.status === 'error')
      ) {
        setShowNoRecommendations(true);
      } else {
        setShowNoRecommendations(false);
      }
    }, [data, setShowNoRecommendations]);

    if (
      showNoRecommendations &&
      data?.body &&
      'status' in data.body &&
      (data.body.status === 'not_found' || data.body.status === 'error') &&
      !isInspectifyOrder
    ) {
      return (
        <NoRecommendationsAvailable
          body={data.body}
          getProperties={() => {
            getProperties(getSearchCriteria());
            setShowNoRecommendations(false);
          }}
        />
      );
    }
    if (isError) {
      return (
        <NoRecommendationsAvailable
          body={{ status: 'error', message: 'An internal error occurred.' }}
          getProperties={() => {
            setShowNoRecommendations(false);
            getProperties(getSearchCriteria());
          }}
        />
      );
    }

    if (isLoading || isUndefined(data)) {
      if (!isLoading && !isRunnable && isUndefined(data)) {
        return (
          <MissingDataWarning
            isSubjectGlaDefined={isSubjectGlaDefined}
            isSubjectLotSizeDefined={isSubjectLotSizeDefined}
            isSubjectLocationDefined={isSubjectLocationDefined}
            onClose={onClose}
          />
        );
      }
      return <LoadingScreenSam overrideText="Loading recommended rental comps..." />;
    }

    const convertedComps = compsToCompSelectionProps(compSeq, comps, subjectComp, newCalcOptions);

    let results: MLSListing[] = [];
    if (
      data.source === 'abode' &&
      data.body &&
      'status' in data.body &&
      data.body.status === 'found'
    ) {
      if ('output' in data.body && data.body.output && 'results' in data.body.output) {
        results = data.body.output.results;
      }
    }

    return (
      <RentalRecCompSelectionInner
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        listings={results}
        subjectComp={subjectComp}
        comps={convertedComps.filter(isDefined)}
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        newCalcOptions={newCalcOptions}
        updateCompsLabel={updateCompsLabel}
        onUpdateComps={onUpdateComps}
        onDeselectAll={onDeselectAll}
        ref={ref}
      />
    );
  },
);
